import { useEffect, useRef } from 'react';
import { useApp } from '../../hooks/useAppState';
import type { AppToast } from '../../hooks/useAppState';
import { theme } from '../../lib/theme';

export function Toast() {
  const { state, dispatch } = useApp();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss every toast after 3s (errors after 5s)
  useEffect(() => {
    if (!state.toast) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const isError = typeof state.toast === 'object'
      ? (state.toast as AppToast).type === 'error'
      : typeof state.toast === 'string' && (/^⚠|^Failed|error/i.test(state.toast));
    timerRef.current = setTimeout(() => {
      dispatch({ type: 'TOAST', message: null } as any);
    }, isError ? 5000 : 3000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [state.toast, dispatch]);

  if (!state.toast) return null;

  // Rich structured toast (error/info with optional code)
  if (typeof state.toast === 'object') {
    const t = state.toast as AppToast;
    const isError = t.type === 'error';
    const borderColor = isError ? '#ef4444' : theme.accent;
    const codeColor   = isError ? '#fca5a5' : theme.accent;

    return (
      <div style={{
        position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
        background: theme.card, border: `1px solid ${borderColor}66`,
        borderRadius: 10, padding: '12px 20px', fontSize: 13,
        color: theme.text, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 9999,
        animation: 'fadeIn .2s ease', minWidth: 280, maxWidth: 480,
      }}>
        {t.code && (
          <div style={{
            fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
            color: codeColor, marginBottom: 4, letterSpacing: '0.04em',
          }}>
            {t.code}
          </div>
        )}
        <div style={{ fontWeight: 600 }}>
          {isError ? '✕ ' : ''}{t.message}
        </div>
      </div>
    );
  }

  // Legacy plain-string toast
  return (
    <div style={{
      position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
      background: theme.card, border: `1px solid ${theme.accent}44`,
      borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 600,
      color: theme.text, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 9999,
      animation: 'fadeIn .2s ease',
    }}>
      {state.toast as string}
    </div>
  );
}
